import { cva } from "class-variance-authority";
import { AlertCircle, ChevronDown, Merge } from "lucide-react";
import { forwardRef, type ComponentProps } from "react";
import { useTranslation } from "react-i18next";

import { ConditionParts } from "./ConditionParts";

import { Badge } from "@/components/ui/v2/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/v2/tooltip";
import type { ConditionBadgeParts, ConditionOverlap, ConditionValue } from "@/features/segments/types";
import { useScrollEdges } from "@/lib/hooks/useScrollEdges";
import { cn } from "@/lib/utils/cn";

const tooltipValueListVariants = cva("no-scrollbar mt-1 max-h-60 overflow-y-auto", {
  variants: {
    fade: {
      none: "",
      top: "[mask-image:linear-gradient(to_bottom,transparent,black_1.5rem)]",
      bottom: "[mask-image:linear-gradient(to_top,transparent,black_1.5rem)]",
      both: "[mask-image:linear-gradient(to_bottom,transparent,black_1.5rem,black_calc(100%-1.5rem),transparent)]",
    },
  },
  defaultVariants: { fade: "none" },
});

const overlapVariants = cva(
  "transition-colors duration-150 ease-out motion-reduce:transition-none",
  {
    variants: {
      overlap: {
        true: "[border-color:var(--badge-info-border)]",
        false: "",
      },
      highlighted: {
        true: "[background:var(--badge-info-bg)]",
        false: "",
      },
    },
    defaultVariants: { overlap: false, highlighted: false },
  },
);

const toFade = (hasMoreAbove: boolean, hasMoreBelow: boolean) => {
  if (hasMoreAbove && hasMoreBelow) return "both";
  if (hasMoreAbove) return "top";
  if (hasMoreBelow) return "bottom";
  return "none";
};

function TooltipValueList({ values }: { values: Extract<ConditionValue, string[]> }) {
  const { ref, hasMoreAbove, hasMoreBelow } = useScrollEdges<HTMLDivElement>();

  return (
    <div ref={ref} className={tooltipValueListVariants({ fade: toFade(hasMoreAbove, hasMoreBelow) })}>
      <ul className="list-disc space-y-0.5 pl-4">
        {values.map((value) => (
          <li key={value}>{value}</li>
        ))}
      </ul>
    </div>
  );
}

type ConditionBadgeProps = ComponentProps<"button"> & {
  parts?: ConditionBadgeParts;
  isInverted: boolean;
  isIncomplete: boolean;
  hasError: boolean;
  overlap?: ConditionOverlap;
};

export const ConditionBadge = forwardRef<HTMLButtonElement, ConditionBadgeProps>(
  ({ parts, isInverted, isIncomplete, hasError, overlap, className, ...props }, ref) => {
    const { t } = useTranslation();

    const badge = (
      <Badge asChild variant="outline" size="large" layout="truncate">
        <button
          ref={ref}
          type="button"
          data-testid="condition-badge"
          data-excluded={isInverted}
          data-overlap-field={overlap?.field}
          data-highlighted={overlap?.isHighlighted}
          onPointerEnter={() => overlap?.onHighlightChange(true)}
          onPointerLeave={() => overlap?.onHighlightChange(false)}
          onFocus={() => overlap?.onHighlightChange(true)}
          onBlur={() => overlap?.onHighlightChange(false)}
          className={cn(
            "group cursor-pointer text-left outline-none",
            "hover-tint-primary focus-visible:ring-ring/50 focus-visible:ring-[3px]",
            "data-[state=open]:border-primary",
            overlapVariants({
              overlap: Boolean(overlap),
              highlighted: overlap?.isHighlighted,
            }),
            isIncomplete && "border-dashed",
            hasError && "border-destructive",
            className,
          )}
          {...props}
        >
          {hasError && (
            <>
              <AlertCircle aria-hidden className="size-4 shrink-0 text-destructive" />
              <span className="sr-only">{t("admin.segments.builder.hasError")}</span>
            </>
          )}
          {overlap && (
            <>
              <Badge aria-hidden variant="info" size="small" className="shrink-0 px-1.5">
                <Merge className="size-3" />
              </Badge>
              <span className="sr-only">{t("admin.segments.builder.overlap.marker")}</span>
            </>
          )}
          {parts ? (
            <ConditionParts parts={parts} truncate />
          ) : (
            <span className="min-w-0 text-muted-foreground">
              {t("admin.segments.builder.selectField")}
            </span>
          )}
          <ChevronDown
            aria-hidden
            className="size-4 shrink-0 text-muted-foreground transition-transform group-aria-expanded:rotate-180"
          />
        </button>
      </Badge>
    );

    if (!parts?.value?.length) return badge;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent className="max-w-xs text-left text-pretty">
          <p className="font-semibold">
            {parts.field}
            {parts.operator && <span className="font-normal text-muted-foreground"> {parts.operator}:</span>}
          </p>
          {Array.isArray(parts.value) ? (
            <TooltipValueList values={parts.value} />
          ) : (
            <p className="mt-1">{parts.value}</p>
          )}
        </TooltipContent>
      </Tooltip>
    );
  },
);

ConditionBadge.displayName = "ConditionBadge";
